import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaEllipsisH } from 'react-icons/fa';
import BoardLookup from '../BoardLookup';
import MenuPortal from '../MenuPortal';
import { ColorSwatches, cardColorId } from '../cardColors';
import { formatCardStamp, isUrgentItem, linkedTasks, taskLabel } from '../boardFormat';
import './BoardCard.css';
import { useI18n } from '../../../../i18n';

const uniqueParts = (...values) => (
	values
		.filter(Boolean)
		.filter((part, index, all) => (
			all.findIndex((item) => String(item).toLowerCase() === String(part).toLowerCase()) === index
		))
);

const BoardCard = (props) => {
	const { t } = useI18n();
	const { tasksById, tasksByRef, assetsById, assetsByRef, taskList, lanes, moveCard } = useContext(BoardLookup);
	const [menuOpen, setMenuOpen] = useState(false);
	const [expanded, setExpanded] = useState(false);
	const [editing, setEditing] = useState(false);
	const [linking, setLinking] = useState(false);
	const [moving, setMoving] = useState(false);
	const [draft, setDraft] = useState('');
	const menuBtnRef = useRef(null);
	const menuPopRef = useRef(null);

	const task = (props.taskId && tasksById[props.taskId]) || null;
	const asset = (props.assetId && assetsById[props.assetId])
		|| (props.asset && assetsByRef[props.asset])
		|| null;
	const description = props.body || (task && task.fault_description) || t('tasks.noDescription');
	const siteRef = props.asset || (task && task.site_ref) || (asset && asset.site_ref) || '';
	const siteAddress = (task && task.site_address) || (asset && asset.site_address) || props.site_address || '';
	const district = props.district || (task && task.district) || (asset && asset.district) || '';
	const place = siteAddress || district || '';
	const locationLine = uniqueParts(place, siteRef);
	const extraLocation = locationLine.filter((part) => part !== siteRef);
	const urgent = isUrgentItem(task || props);
	const stamp = formatCardStamp(props.dateCreated || (task && task.date_created));
	const owner = props.name || '';
	const assetId = props.assetId || (asset && asset.assetId) || (task && task.assetId);
	const color = cardColorId(props.cardColor);
	const linked = linkedTasks(props, tasksById, tasksByRef)[0] || null;
	const currentLaneId = (lanes || []).find((lane) => (
		(lane.cards || []).some((card) => card.id === props.id)
	));
	const otherLanes = (lanes || []).filter((lane) => !currentLaneId || lane.id !== currentLaneId.id);

	const closeMenu = () => {
		setMenuOpen(false);
		setLinking(false);
		setMoving(false);
	};

	useEffect(() => {
		if (!menuOpen) return undefined;
		const onDoc = (event) => {
			const target = event.target;
			if (menuBtnRef.current && menuBtnRef.current.contains(target)) return;
			if (menuPopRef.current && menuPopRef.current.contains(target)) return;
			closeMenu();
		};
		document.addEventListener('mousedown', onDoc);
		return () => document.removeEventListener('mousedown', onDoc);
	}, [menuOpen]);

	const setColor = (next) => {
		if (props.onChange) props.onChange({ id: props.id, cardColor: next });
		closeMenu();
	};

	const saveEdit = () => {
		const note = (draft || '').trim();
		setEditing(false);
		if (!note || note === description || !props.onChange) return;
		props.onChange({ id: props.id, body: note, title: note });
	};

	const linkTask = (taskId) => {
		const selected = (taskList || []).find((item) => item.taskId === taskId);
		if (!selected || !props.onChange) return;
		props.onChange({
			id: props.id,
			taskId: selected.taskId,
			task: selected.task_ref,
			assetId: selected.assetId || props.assetId,
			asset: selected.site_ref || props.asset,
			district: selected.district || props.district,
			urgent_fault: selected.urgent_fault,
		});
		closeMenu();
	};

	const moveToLane = (laneId) => {
		if (laneId && moveCard) moveCard(props.id, laneId);
		closeMenu();
	};

	return (
		<article className={`BoardCard ${color}${expanded ? ' is-open' : ''}${urgent ? ' is-urgent' : ''}`}>
			<div className="board-card-main">
				{editing ? (
					<textarea
						className="board-card-edit"
						value={draft}
						autoFocus
						rows={3}
						onClick={(event) => event.stopPropagation()}
						onChange={(event) => setDraft(event.target.value)}
						onBlur={saveEdit}
						onKeyDown={(event) => {
							if (event.key === 'Enter' && !event.shiftKey) {
								event.preventDefault();
								saveEdit();
							}
							if (event.key === 'Escape') setEditing(false);
						}}
					/>
				) : (
					<button
						type="button"
						className={`board-card-title${expanded ? ' is-expanded' : ''}`}
						title={description}
						onClick={(event) => {
							event.stopPropagation();
							setExpanded((open) => !open);
						}}
					>
						{description}
					</button>
				)}
				<div className="board-card-menu">
					<button
						ref={menuBtnRef}
						type="button"
						className="board-card-menu-btn"
						aria-label="Card actions"
						aria-expanded={menuOpen}
						onMouseDown={(event) => event.stopPropagation()}
						onClick={(event) => {
							event.stopPropagation();
							setMenuOpen((open) => !open);
							setLinking(false);
							setMoving(false);
						}}
					>
						<FaEllipsisH />
					</button>
					{menuOpen && (
						<MenuPortal anchorRef={menuBtnRef} menuRef={menuPopRef}>
							<div className="board-card-menu-pop">
								<button
									type="button"
									role="menuitem"
									onClick={(event) => {
										event.stopPropagation();
										closeMenu();
										setDraft(description === t('tasks.noDescription') ? '' : description);
										setEditing(true);
									}}
								>
									{t('board.editNote')}
								</button>
								<p className="board-card-menu-label">{t('board.colour')}</p>
								<ColorSwatches value={color} onChange={setColor} />
								{!linked && (
									linking ? (
										<select
											className="board-card-link-select"
											defaultValue=""
											onClick={(event) => event.stopPropagation()}
											onChange={(event) => {
												event.stopPropagation();
												if (event.target.value) linkTask(event.target.value);
											}}
										>
											<option value="">{t('board.chooseTask')}</option>
											{(taskList || []).map((item) => (
												<option key={item.taskId} value={item.taskId}>
													{taskLabel(item.task_ref)}
												</option>
											))}
										</select>
									) : (
										<button
											type="button"
											role="menuitem"
											onClick={(event) => {
												event.stopPropagation();
												setLinking(true);
											}}
										>
											Link task
										</button>
									)
								)}
								{otherLanes.length > 0 && (
									moving ? (
										<select
											className="board-card-link-select"
											defaultValue=""
											onClick={(event) => event.stopPropagation()}
											onChange={(event) => {
												event.stopPropagation();
												if (event.target.value) moveToLane(event.target.value);
											}}
										>
											<option value="">Move to…</option>
											{otherLanes.map((lane) => (
												<option key={lane.id} value={lane.id}>{lane.title}</option>
											))}
										</select>
									) : (
										<button
											type="button"
											role="menuitem"
											onClick={(event) => {
												event.stopPropagation();
												setMoving(true);
											}}
										>
											Move
										</button>
									)
								)}
								<button
									type="button"
									role="menuitem"
									className="is-danger"
									onClick={(event) => {
										event.stopPropagation();
										closeMenu();
										props.onDelete(event);
									}}
								>
									Delete
								</button>
							</div>
						</MenuPortal>
					)}
				</div>
			</div>

			{(urgent || linked) && (
				<div className="board-card-chips">
					{urgent && <span className="board-card-urgent">Urgent</span>}
					{linked && (
						linked.taskId ? (
							<Link
								className="board-card-task"
								to={`/task_det/${linked.taskId}`}
								onClick={(e) => e.stopPropagation()}
							>
								↗ Task {linked.label}
							</Link>
						) : (
							<span className="board-card-task">{`↗ Task ${linked.label}`}</span>
						)
					)}
				</div>
			)}

			{locationLine.length > 0 && (
				<div className="board-card-context">
					{extraLocation.map((part) => (
						<span key={part} className="board-card-place">{part}</span>
					))}
					{siteRef && (
						assetId ? (
							<Link className="board-card-site" to={`/asset_det/${assetId}`} onClick={(e) => e.stopPropagation()}>
								{siteRef}
							</Link>
						) : (
							<span className="board-card-site">{siteRef}</span>
						)
					)}
				</div>
			)}

			{(owner || stamp.date) && (
				<div className="board-card-footer">
					<span className="board-card-owner">{owner}</span>
					{stamp.date && <span className="board-card-when">{stamp.date}</span>}
				</div>
			)}
		</article>
	);
};

export default BoardCard;
